import { createContext, useCallback, useContext, useEffect, useRef } from 'react';

/**
 * What the page on screen can be ASKED TO OPEN by the AI rail.
 *
 * ⭐⭐⭐ THE PAGE OWNS THE RECORD, THE MODEL OWNS ONLY THE FIELDS. The assistant is never told the
 *    id of the property or deal being looked at - `AssistantPanel` sends a rendered fact block and
 *    nothing else, deliberately. So an action says "open the brochure editor", never "open the
 *    brochure editor for 7a3f...", and the handler registered below is the one belonging to the
 *    page that is actually mounted. A model that cannot name a record cannot aim a document at the
 *    wrong one.
 *
 * ⭐⭐ IT OPENS THE PAGE'S OWN EXISTING DIALOG. Nothing here generates, saves or files anything:
 *    the handlers are the same `openBrochure()` / `openLoiDialog()` the buttons already call, so
 *    every fact gate, every 400 refusal in `loiBuilderService` and every filing rule is untouched.
 *    A chat sentence reaches exactly as far as a click on the button beside it.
 *
 * ⚠️ SEPARATE FROM `PageSubjectContext` ON PURPOSE. That one is keyed on
 *    `JSON.stringify(subject.facts)` to stop a page that builds its subject inline from looping;
 *    a handler is a new function every render and would break that key on every single render.
 *    Functions therefore live here, behind a ref, and are never part of any dependency key.
 */

/**
 * Field names deliberately match `LoiPrefill` and `LeaseProposalPrefill`, so a handler can spread
 * them straight in. ⛔ ONE FLAT SHAPE for every action: a handler takes what its own dialog
 * understands and ignores the rest, which is what keeps `run` from needing to know anything about
 * which dialog it is dispatching to.
 */
export interface PageActionFields {
  purchasePrice?: number;
  emdAmount?: number;
  /** YYYY-MM-DD. The dialog's expiration input is `type="date"` and takes nothing else. */
  expiration?: string;
  sellerName?: string;
  counterToSellerCounterNo?: number;
  /**
   * The 97th. ⭐ Who the letter GREETS when that is not the seller - his Olive Garden convention
   * heads the owner of record and greets the listing agent.
   */
  salutationName?: string;
  /** Which of his two Buyer Profile sentences the letter carries. */
  buyerProfile?: 'all_cash_1031' | 'financed_1031' | 'none';
  /** How the price is paid. `all_cash` prints his three-line whole-price block. */
  balanceKind?: 'all_cash' | 'cash_balance' | 'cash' | 'new_loan' | 'seller_financing';
  downPayment?: number;
  /** ⛔ A 1031 letter the builder REFUSES without its down-leg close date. */
  is1031?: boolean;
  downLegCloseDate?: string;
  /**
   * ⛔⛔ THE PROPERTY ITSELF. With a record on screen `AssistantPanel` STRIPS which building it is
   *    (the address and its parts - there an address is an IDENTIFIER and the page owns it), and
   *    strips the facts about the building too, except for the LOI (the 114th, his answer
   *    2026-10-01: his stated SF, acres and description reach the letter, and its correction note
   *    says where they differ). See `PROPERTY_IDENTITY_FIELDS` / `PROPERTY_FACT_FIELDS` there and
   *    the tests that pin them.
   */
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  county?: string;
  buildingSf?: number;
  acres?: number;
  description?: string;
  /**
   * ⭐ The 101st - his September RE line. ⛔ PROPERTY IDENTITY like the address: stripped by
   *   `AssistantPanel` when a record is on screen. `tenantName` below is the LEASE proposal's field,
   *   which is why the LOI's tenant has its own name.
   */
  reLineTenant?: string;
  lotSf?: number;
  reLineLocation?: string;
  /**
   * 🚨🚨 The 101st - the parties he names, which the chat door dropped (driven live with his real
   *    Applebee's terms). Terms he STATES, so they pass whether or not a record is on screen.
   *    `listingAttn` and `sellersBrokerage` are the names the LOI dialog has always read.
   */
  buyerNames?: string[];
  signatureHeading?: string;
  careOf?: string;
  listingAttn?: string;
  sellersBrokerage?: string;
  sellerIsEntity?: boolean;
  recipientLines?: string[];
  /** The 101st - his wording era, his due-diligence list, and a 1031 not yet in escrow. */
  wording?: 'september_2026' | 'june_2026';
  ddList?: 'standard' | 'single_tenant_restaurant' | 'vacant' | 'restaurant_pad';
  downLegNotInEscrow?: boolean;
  /**
   * ⭐ LOI-S5 (the 127th) - his revised Olive Garden letters, each choice OFF unless he states it. The three dates are
   *   calendar days (YYYY-MM-DD) because the dialog's boxes are date boxes; the letter prints them in his long form.
   */
  revisesLetterDated?: string;
  revisesRevisedLetter?: boolean;
  downLegExtensionDays?: number;
  downLegExtensionFrom?: string;
  downLegProtection?: boolean;
  ddFromDeliverables?: boolean;
  coeDaysAfterDd?: number;
  coeOutsideDate?: string;
  /**
   * ⭐ The LOI override - his words about SECTIONS of the letter. `sectionInstructions` rides with `open_loi_dialog`
   *   (at most ten); `instruction` is `revise_open_loi`'s one change to the letter already open. ⛔ Terms he STATES,
   *   never property identity, so neither is stripped on a record page - and the dialog drafts each for his accept.
   */
  sectionInstructions?: string[];
  instruction?: string;
  /** LP1.4 - the lease proposal's terms. */
  assetType?: 'office_suite' | 'industrial_flex' | 'industrial_condo' | 'retail_center' | 'ios_yard';
  tenantName?: string;
  landlordName?: string;
  squareFeet?: number;
  ratePerSf?: number;
  monthlyRent?: number;
  termMonths?: number;
  /** YYYY-MM-DD. The dialog's commencement input is `type="date"` and takes nothing else. */
  commencementDate?: string;
  annualIncreasePercent?: number;
  rentStructure?: 'modified_gross' | 'nnn' | 'gross';
  /**
   * The 98th - the listing proposal (exec summary).
   *
   * ⛔ `addressLine`, `propertyType` and `zoning` are PROPERTY IDENTITY and are stripped when a
   *   record is on screen, exactly as the LOI's are - the page owns which building it is.
   * ⭐ The owner, the subject, the scope and the price are things HE states about the engagement,
   *   so they pass either way. Same rule that lets the LOI carry a seller name he dictated.
   */
  ownerName?: string;
  ownerAddressLines?: string[];
  addressLine?: string;
  propertyType?: string;
  zoning?: string;
  propertyClass?: string;
  subject?: string;
  scopeDescription?: string;
  askingPrice?: number;
  askingPriceHigh?: number;
  /**
   * The 99th - the brochure / OM editor's own choices. ⛔ NOT property identity: a brochure is
   * rendered from the record's own data and photographs, so these say what KIND of document and
   * what it looks like, never which building. They are not stripped.
   */
  format?: 'brochure' | 'flyer';
  dealKind?: 'sale' | 'lease';
  theme?: 'apex' | 'manchester' | 'classic';
  coverHeadline?: string;
  coverSubhead?: string;
  tagline?: string;
  /**
   * ⭐ The 100th - the PDFs he attached in THIS conversation, offered to the LOI dialog's OM read.
   * ⛔⛔ Added by `AssistantPanel` from his OWN messages, never by the model - the tool has no such
   *    parameter, so a model cannot name a file. And NOT property identity: an attachment says
   *    nothing about which building is on screen, so it survives the strip on a record page.
   */
  omAttachments?: { storagePath: string; fileName: string }[];
}

export type PageActionName =
  | 'open_brochure_editor'
  | 'open_loi_dialog'
  | 'open_lease_proposal_dialog'
  /** The 98th - the EXEC SUMMARY, the owner-facing letter that wins the listing. */
  | 'open_listing_proposal_dialog'
  /**
   * ⭐ The LOI override - a change to the LOI ALREADY OPEN. Registered by the open dialog itself, so with no letter open
   *   nothing answers and the rail says so.
   */
  | 'revise_open_loi';

export type PageActionHandler = (fields: PageActionFields) => void;

interface Ctx {
  register: (name: PageActionName, handler: PageActionHandler) => () => void;
  /**
   * A host for the same action when there is NO record on screen. Registered once by the rail,
   * never by a page. ⛔ A SEPARATE MAP, never the same one: a fallback that could clobber a page
   * handler (or be clobbered by one) would make which dialog opens depend on mount order.
   */
  registerFallback: (name: PageActionName, handler: PageActionHandler) => () => void;
  /**
   * Returns false when nothing could run it - the caller SAYS so rather than going quiet.
   *
   * `recordless` selects WHICH host, and the two are disjoint on purpose - see the run
   * implementation below for why a request must never cross between them.
   */
  run: (name: PageActionName, fields: PageActionFields, recordless?: boolean) => boolean;
}

/**
 * ⚠️ A working no-op default rather than `undefined`, for the reason `PageSubjectContext` gives:
 * the hook is called from the rail, which Layout mounts on every page, and throwing outside a
 * provider would turn a missing optional feature into a blank app.
 */
const PageActionCtx = createContext<Ctx>({
  register: () => () => {},
  registerFallback: () => () => {},
  run: () => false,
});

export function PageActionProvider({ children }: { children: React.ReactNode }) {
  const handlers = useRef(new Map<PageActionName, PageActionHandler>());
  /** ⭐ The rail's recordless hosts. Never populated by a page. */
  const fallbacks = useRef(new Map<PageActionName, PageActionHandler>());

  const register = useCallback((name: PageActionName, handler: PageActionHandler) => {
    handlers.current.set(name, handler);
    return () => {
      // 🚨 ONLY IF IT IS STILL MINE. React mounts the next page's component before unmounting the
      // previous one on some transitions, so a blind `delete` in the outgoing page's cleanup
      // would remove the INCOMING page's freshly registered handler and leave the app with no
      // action at all - a feature that works until you navigate, which is the hardest kind to see.
      if (handlers.current.get(name) === handler) handlers.current.delete(name);
    };
  }, []);

  const registerFallback = useCallback((name: PageActionName, handler: PageActionHandler) => {
    fallbacks.current.set(name, handler);
    return () => {
      // The same "only if it is still mine" rule as `register`, for the same reason.
      if (fallbacks.current.get(name) === handler) fallbacks.current.delete(name);
    };
  }, []);

  /**
   * ⛔⛔ DISJOINT, NOT A CHAIN. A recordless ask runs ONLY the fallback; an ask about a record
   *    runs ONLY that page. Chaining them in either direction is a real defect:
   *      - falling back on a record page that has no LOI dialog would hand him a BLANK letter
   *        while he believes it is about the building in front of him - filled-looking and
   *        wrong, which is the 84th's finding in its other direction;
   *      - promoting a recordless ask to whatever page happens to be mounted would bind a letter
   *        to a record he never named, which is the `fileToTarget` failure exactly.
   */
  const run = useCallback((name: PageActionName, fields: PageActionFields, recordless = false) => {
    const handler = (recordless ? fallbacks : handlers).current.get(name);
    if (!handler) return false;
    handler(fields);
    return true;
  }, []);

  return (
    <PageActionCtx.Provider value={{ register, registerFallback, run }}>
      {children}
    </PageActionCtx.Provider>
  );
}

/**
 * Called by a record page to offer one of its dialogs to the rail.
 *
 * ⭐ THE CLEANUP IS LOAD-BEARING, exactly as it is in `useSetPageSubject`. Without it, navigating
 * from a deal to the map would leave the deal's LOI opener registered, and a chat sentence would
 * open a dialog for a record the operator is no longer looking at.
 *
 * ⚠️ The handler is held in a ref and re-pointed on EVERY render, while the effect depends only on
 * the name. That combination is the point: the registration is stable (so it is not torn down and
 * rebuilt constantly, which would widen the window where no handler exists), and the function it
 * runs is always the current render's closure - never a stale one holding last render's prefill.
 */
export function usePageAction(name: PageActionName, handler: PageActionHandler): void {
  const { register } = useContext(PageActionCtx);
  const latest = useRef(handler);
  latest.current = handler;

  useEffect(
    () => register(name, (fields) => latest.current(fields)),
    [name, register]
  );
}

/**
 * Called ONCE by the rail, to host an action for pages that have no record.
 *
 * ⛔ NOT for a record page - a page uses `usePageAction`. Keeping the two hooks distinct is what
 *   stops a recordless letter and a letter about a property ever reaching the same handler.
 */
export function usePageActionFallback(name: PageActionName, handler: PageActionHandler): void {
  const { registerFallback } = useContext(PageActionCtx);
  const latest = useRef(handler);
  latest.current = handler;

  useEffect(
    () => registerFallback(name, (fields) => latest.current(fields)),
    [name, registerFallback]
  );
}

/** Read by the rail. */
export function usePageActionRunner(): Ctx['run'] {
  return useContext(PageActionCtx).run;
}
